import axios from 'axios'

import { resolveCommunityIdFromHost } from '@/lib/resolve-community-id'

import { getCommunityApiClient } from '@/services/community-api.client'
import type { TenantConfig, TenantCustomization, UpdateMyMediaDefaultsPayload } from '@/services/tenant.service'
import { getTenantFromWindow } from '@/utils/tenant.utils'

export interface CommunityMediaDefaults {
  defaultAvatarUrl: string | null
  defaultThumbnailUrl: string | null
  defaultFaviconUrl: string | null
  defaultLogoUrl: string | null
  socialLinks: { platform: string; url: string }[] | null
  senderName: string | null
  senderEmail: string | null
  primaryColor: string | null
}

class CommunityMediaDefaultService {
  async getByCommunityId(communityId: string): Promise<CommunityMediaDefaults> {
    const response = await getCommunityApiClient().get<{ mediaDefaults: CommunityMediaDefaults }>(
      `/communities/${communityId}/media-defaults`
    )
    return response.data.mediaDefaults
  }

  async getBySubdomain(subdomain: string): Promise<CommunityMediaDefaults | null> {
    try {
      const communityResponse = await getCommunityApiClient().get<{ id: string }>(
        `/communities/subdomain/${encodeURIComponent(subdomain)}`
      )
      return this.getByCommunityId(communityResponse.data.id)
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null
      }
      throw error
    }
  }

  async updateByCommunityId(
    communityId: string,
    payload: UpdateMyMediaDefaultsPayload
  ): Promise<CommunityMediaDefaults> {
    const response = await getCommunityApiClient().put<{ mediaDefaults: CommunityMediaDefaults }>(
      `/communities/${communityId}/media-defaults`,
      payload
    )
    return response.data.mediaDefaults
  }

  async updateForCurrentHost(payload: UpdateMyMediaDefaultsPayload): Promise<CommunityMediaDefaults> {
    const communityId = await resolveCommunityIdFromHost()
    if (!communityId) {
      throw new Error('Community not found for current host')
    }
    return this.updateByCommunityId(communityId, payload)
  }

  async getForCurrentHost(): Promise<CommunityMediaDefaults | null> {
    const subdomain = getTenantFromWindow()
    if (!subdomain || subdomain === 'default') {
      return null
    }
    return this.getBySubdomain(subdomain)
  }

  /** Remove fsds-be mediaDefaults; community sites use eztech-be only. */
  private stripFsdsMediaDefaults(customization: TenantCustomization | undefined): TenantCustomization {
    if (!customization) {
      return {}
    }
    const rest = { ...customization }
    delete rest.mediaDefaults
    return rest
  }

  mergeIntoCustomization(
    customization: TenantCustomization | undefined,
    mediaDefaults: CommunityMediaDefaults | null
  ): TenantCustomization {
    // Community config from eztech-be may already include merged mediaDefaults.
    // Only strip legacy fsds-be values when replacing with a fresh media-defaults response.
    if (!mediaDefaults) {
      return customization ?? {}
    }

    const base = this.stripFsdsMediaDefaults(customization)

    return {
      ...base,
      mediaDefaults: {
        defaultAvatarUrl: mediaDefaults.defaultAvatarUrl ?? undefined,
        defaultThumbnailUrl: mediaDefaults.defaultThumbnailUrl ?? undefined,
        defaultFaviconUrl: mediaDefaults.defaultFaviconUrl ?? undefined,
        defaultLogoUrl: mediaDefaults.defaultLogoUrl ?? undefined,
        socialLinks: mediaDefaults.socialLinks ?? undefined,
        senderName: mediaDefaults.senderName ?? undefined,
        senderEmail: mediaDefaults.senderEmail ?? undefined,
        primaryColor: mediaDefaults.primaryColor ?? undefined,
      },
    }
  }

  /** Apply media default primary color onto tenant theme for runtime styling. */
  mergeThemeWithMediaDefaults(
    theme: TenantConfig['theme'] | undefined,
    mediaDefaults: CommunityMediaDefaults | null
  ): TenantConfig['theme'] {
    const base = { ...(theme ?? {}) }
    const primary = mediaDefaults?.primaryColor?.trim()
    if (primary) {
      base.primaryColor = primary
    }
    return base
  }
}

export const communityMediaDefaultService = new CommunityMediaDefaultService()
export default communityMediaDefaultService
